import { StandardEvents } from '@shopify/events';

/**
 * Formatos que acepta el logo: los que la previsualización puede dibujar en
 * cualquier navegador. Fuera quedan SVG (puede llevar código dentro) y HEIC
 * (Chrome no lo muestra). Se comprueban el tipo y la extensión, porque
 * algunos sistemas no informan el tipo del archivo.
 */
const LOGO_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const LOGO_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp'];

/**
 * Píxeles máximos de la copia limpia del logo. Safari en iPhone no dibuja
 * lienzos más grandes; una foto mayor se reduce en proporción hasta entrar.
 */
const LOGO_MAX_PIXELS = 16_000_000;

/**
 * Nombre de cada escena para el cliente: en el selector de dónde va un ícono
 * nuevo y en la lista de íconos agregados.
 */
const ZONE_LABELS = {
  virola: 'Virola',
  cuerpo: 'Costado',
  termo: 'Costado',
  bombilla: 'Caña',
  cuerpo2: 'Otro costado',
  termo2: 'Otro costado',
};
const ICON_TARGET_PROMPT = '¿Dónde lo agregás?';

/** @param {string} zone */
function zoneLabel(zone) {
  return ZONE_LABELS[/** @type {keyof typeof ZONE_LABELS} */ (zone)] ?? '';
}

/**
 * Bloque de grabado láser.
 *
 * No cambia variantes por su cuenta: marca el radio equivalente del selector
 * del tema —que sigue en la página, oculto— y deja que Horizon haga el resto
 * (variante, URL, precio, stock, carrito). Aquí solo vive lo que el tema no
 * sabe: qué campos se ven, qué se envía como propiedad y la previsualización.
 */
class MrEngraving extends HTMLElement {
  #controller = new AbortController();
  #section = null;
  /** @type {HTMLInputElement | HTMLTextAreaElement | null} */
  #activeField = null;
  /** Logo que muestra la previsualización, para no recargarlo sin cambios. */
  #logoFile = null;
  /** Logo del otro costado en la previsualización. */
  #logo2File = null;
  /**
   * Turno de la última imagen elegida, por campo: una más nueva deja sin
   * efecto la anterior del mismo campo.
   *
   * @type {WeakMap<HTMLInputElement, number>}
   */
  #logoTurns = new WeakMap();
  /** ¿Está bloqueado el botón de añadir al carrito por falta de datos? */
  #blocked = false;
  /** @type {MutationObserver | null} */
  #buttonObserver = null;
  #nudgeTimer = 0;
  /**
   * Íconos agregados, en orden. Un mismo ícono puede estar varias veces: cada
   * copia lleva su propio uid («bloque~n») para moverla y quitarla aparte.
   *
   * @type {{ uid: string, name: string }[]}
   */
  #iconList = [];
  #iconCount = 0;
  /** Escena donde entra el próximo ícono, cuando hay más de una. */
  #iconTarget = '';

  connectedCallback() {
    this.#section = this.closest('.shopify-section');

    const { signal } = this.#controller;

    this.addEventListener('change', this.#handleChange, { signal });
    this.addEventListener('input', this.#handleInput, { signal });
    this.addEventListener('click', this.#handleClick, { signal });
    this.addEventListener('focusin', this.#handleFocus, { signal });
    this.addEventListener('keydown', this.#handleKeydown, { signal });
    this.addEventListener('mr-preview:zone', this.#handlePreviewZone, { signal });
    this.#section?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    document.addEventListener('click', this.#handleOutsideClick, { signal });
    // En window y en captura: el tema reparte sus «on:click» y «on:submit»
    // desde document, también en captura, y se registra antes que este
    // bloque. Escuchando en window el toque se frena antes de que llegue a
    // document, así no arranca ni la animación de la imagen volando al
    // carrito ni el alta.
    window.addEventListener('click', this.#handleBlockedClick, { signal, capture: true });
    window.addEventListener('submit', this.#handleSubmit, { signal, capture: true });

    // El selector de archivos del sistema muestra solo imágenes admitidas
    for (const logo of this.querySelectorAll('[data-mr-logo], [data-mr-logo2]')) {
      logo.setAttribute('accept', [...LOGO_TYPES, ...LOGO_EXTENSIONS].join(','));
    }

    // El tema repinta el botón al cambiar de variante y le quita la marca;
    // mientras falten datos del grabado, se le vuelve a poner.
    this.#buttonObserver = new MutationObserver(() => {
      if (this.#blocked) this.#setCartBlocked(true);
    });
    if (this.#section) {
      this.#buttonObserver.observe(this.#section, {
        subtree: true,
        attributes: true,
        attributeFilter: ['disabled', 'data-mr-blocked'],
      });
    }

    this.#syncChoice(this.dataset.open === 'true' || this.#isEngraved);
    this.#syncSecond();
    this.#togglePanels();
    this.#syncFont();
    this.#syncPreview();
    this.#syncCounter();
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#buttonObserver?.disconnect();
    this.#setCartBlocked(false);
  }

  get #form() {
    const id = this.dataset.formId;
    return id ? /** @type {HTMLFormElement | null} */ (document.getElementById(id)) : null;
  }

  /**
   * Campo de texto por selector, sea input o textarea.
   *
   * El texto a grabar admite saltos de línea, así que es un textarea: dar por
   * hecho que era un input dejaba el formulario colgado en el primer tramo.
   *
   * @param {string} selector
   * @returns {HTMLInputElement | HTMLTextAreaElement | null}
   */
  #field(selector) {
    const element = this.querySelector(selector);
    return element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement ? element : null;
  }

  /**
   * ¿El texto de ese campo admite saltos de línea? Solo el que va al cuerpo
   * del mate o al del termo.
   *
   * @param {HTMLElement} field
   */
  #allowsLineBreaks(field) {
    // Solo el texto que va al cuerpo: en el aro un salto no tiene dónde ir
    if (field.matches('[data-mr-text2]')) return false;
    if (field.matches('[data-mr-text3], [data-mr-text4]')) return true;

    const zones = this.#baseZones;
    return zones.length === 1 && ['cuerpo', 'termo'].includes(zones[0]);
  }

  /** Zonas de la tarjeta elegida, sin el otro costado. */
  get #baseZones() {
    return (this.#engravingChoice?.dataset.mrZone || 'virola').split(',').map((zone) => zone.trim());
  }

  /**
   * Zonas que se graban: las de la tarjeta y, con la casilla marcada, el otro
   * costado («cuerpo2» o «termo2», que repiten el área del costado).
   */
  get #zones() {
    const zones = this.#baseZones;
    return this.#secondActive ? [...zones, this.#secondZone] : zones;
  }

  /** Escena del otro costado según la tarjeta: la del termo o la del mate. */
  get #secondZone() {
    return this.#baseZones.includes('termo') ? 'termo2' : 'cuerpo2';
  }

  /** @returns {HTMLInputElement | null} */
  get #secondToggle() {
    const toggle = this.querySelector('[data-mr-second-toggle]');
    return toggle instanceof HTMLInputElement ? toggle : null;
  }

  /** ¿Se graba también el otro costado? */
  get #secondActive() {
    const toggle = this.#secondToggle;
    const wrap = this.querySelector('[data-mr-second-wrap]');
    return Boolean(toggle?.checked && wrap instanceof HTMLElement && !wrap.hidden);
  }

  /**
   * La casilla del otro costado solo se ve si la tarjeta elegida lo admite
   * (data-mr-second). Pinta su precio y, si se oculta, se desmarca.
   */
  #syncSecond() {
    const wrap = this.querySelector('[data-mr-second-wrap]');
    const toggle = this.#secondToggle;
    if (!(wrap instanceof HTMLElement) || !toggle) return;

    const choice = this.#engravingChoice;
    const available = Boolean(this.dataset.open === 'true' && choice?.dataset.mrSecond);

    wrap.hidden = !available;
    if (!available) toggle.checked = false;

    const price = wrap.querySelector('[data-mr-second-price]');
    if (price instanceof HTMLElement) {
      price.replaceChildren();
      if (available && choice?.dataset.mrSecondPrice) {
        price.append(`+${choice.dataset.mrSecondPrice}`);
        if (choice.dataset.mrSecondWas) {
          const was = document.createElement('s');
          was.className = 'mr-card__was';
          was.textContent = choice.dataset.mrSecondWas;
          price.append(' ', was);
        }
      }
    }
  }

  /**
   * Qué campos de texto tienen sentido con la opción elegida:
   *
   *   virola          dos frases sobre el aro
   *   cuerpo          un texto
   *   virola+cuerpo   dos frases en el aro y una tercera para el costado
   */
  #toggleTextFields() {
    const zones = this.#baseZones;
    const ring = zones.includes('virola');
    const both = zones.length > 1;
    const second = this.#secondActive;

    for (const field of this.querySelectorAll('[data-mr-field]')) {
      if (!(field instanceof HTMLElement)) continue;
      if (field.dataset.mrField === 'text2') field.hidden = !ring;
      if (field.dataset.mrField === 'text3') field.hidden = !both;
      if (field.dataset.mrField === 'side2') field.hidden = !second;
    }
  }

  /** Radio marcado de las tarjetas de grabado. */
  get #engravingChoice() {
    return /** @type {HTMLInputElement | null} */ (this.querySelector('[data-mr-cards="engraving"] input:checked'));
  }

  get #isEngraved() {
    const choice = this.#engravingChoice;
    return Boolean(choice && choice.value !== this.dataset.engravingNone);
  }

  /** @param {Event} event */
  #handleChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;

    if (target.matches('[data-mr-second-toggle]')) {
      const choice = this.#engravingChoice;
      if (choice) {
        this.#selectOption(choice.dataset.mrOption, target.checked ? choice.dataset.mrSecond : choice.value);
      }
      this.#togglePanels();
      this.#syncPreview();
      return;
    }

    if (target.matches('.mr-card__input')) {
      const engraving = target.dataset.mrOption === this.dataset.engravingOption;

      // La casilla del otro costado sigue marcada si la nueva tarjeta lo admite
      if (engraving) this.#syncSecond();
      const value = engraving && this.#secondActive && target.dataset.mrSecond ? target.dataset.mrSecond : target.value;
      this.#selectOption(target.dataset.mrOption, value);

      // Sin grabado no debe dejar un tiempo de taller elegido por detrás
      if (target.dataset.mrOption === this.dataset.engravingOption && !this.#isEngraved) {
        this.#selectOption(this.dataset.timeOption, this.dataset.timeNone);
      }

      this.#togglePanels();
      this.#applyMaxLength(target);
      this.#syncPreview();
      return;
    }

    if (target.matches('.mr-font__input')) {
      this.#syncFont();
      this.#closeFontSheet();
      return;
    }

    if (target.matches('[data-mr-logo], [data-mr-logo2]')) {
      this.#processLogo(target);
    }
  };

  /** @param {Event} event */
  #handleFocus = (event) => {
    const target = event.target;
    if (target instanceof HTMLTextAreaElement || target instanceof HTMLInputElement) {
      if (target.matches('[data-mr-text], [data-mr-text2], [data-mr-text3], [data-mr-text4]')) this.#activeField = target;
    }
  };

  /**
   * En la virola el texto sigue una curva: un salto de línea no tiene dónde ir,
   * así que se impide al escribir y se limpia al pegar.
   *
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    if (event.key === 'Escape' && this.querySelector('[data-mr-fonts]')?.getAttribute('data-open') === 'true') {
      this.#closeFontSheet();
      const toggle = this.querySelector('[data-mr-fonts-toggle]');
      if (toggle instanceof HTMLElement) toggle.focus();
      return;
    }

    if (event.key !== 'Enter') return;
    if (
      !(event.target instanceof HTMLElement) ||
      !event.target.matches('[data-mr-text], [data-mr-text2], [data-mr-text3], [data-mr-text4]')
    ) {
      return;
    }
    if (this.#allowsLineBreaks(event.target)) return;

    event.preventDefault();
  };

  /** @param {Event} event */
  #handleInput = (event) => {
    if (
      !(event.target instanceof HTMLElement) ||
      !event.target.matches('[data-mr-text], [data-mr-text2], [data-mr-text3], [data-mr-text4]')
    ) {
      return;
    }

    const field = event.target;
    if ((field instanceof HTMLTextAreaElement || field instanceof HTMLInputElement) && !this.#allowsLineBreaks(field)) {
      field.value = field.value.replace(/\s*\n\s*/g, ' ');
    }

    this.#syncCounter();
    this.#syncFontSamples();
    this.#syncPreview();
    this.#checkNumbers();
    this.#togglePanels();
  };

  /** @param {Event} event */
  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const choice = target.closest('[data-mr-choice]');
    if (choice instanceof HTMLElement) {
      this.#setOpen(choice.dataset.mrChoice === 'custom');
      return;
    }

    const symbol = target.closest('[data-mr-symbol]');
    if (symbol instanceof HTMLElement) {
      this.#insertSymbol(symbol.dataset.mrSymbol ?? '');
      return;
    }

    // Dónde entra el próximo ícono
    const option = target.closest('[data-mr-icon-target-option]');
    if (option instanceof HTMLElement) {
      this.#iconTarget = option.dataset.mrIconTargetOption ?? '';
      this.#syncPreview();
      return;
    }

    // Cada toque agrega una copia más, aunque el ícono ya esté
    const icon = target.closest('[data-mr-icon]');
    if (icon instanceof HTMLElement && icon.dataset.mrIcon) {
      this.#iconCount += 1;
      this.#iconList.push({
        uid: `${icon.dataset.mrIcon}~${this.#iconCount}`,
        name: icon.dataset.mrIconName || icon.dataset.mrIcon,
      });
      this.#renderIconList();
      this.#togglePanels();
      this.#syncPreview();
      return;
    }

    const remove = target.closest('[data-mr-icon-remove]');
    if (remove instanceof HTMLElement) {
      this.#iconList = this.#iconList.filter((item) => item.uid !== remove.dataset.mrIconRemove);
      this.#renderIconList();
      this.#togglePanels();
      this.#syncPreview();
      return;
    }

    if (target.closest('[data-mr-fonts-toggle]')) {
      const sheet = this.querySelector('[data-mr-fonts]');
      const toggle = this.querySelector('[data-mr-fonts-toggle]');
      const open = sheet?.getAttribute('data-open') !== 'true';

      sheet?.setAttribute('data-open', String(open));
      toggle?.setAttribute('aria-expanded', String(open));
    }
  };

  /**
   * El cliente tocó una escena de la previsualización: los íconos nuevos van
   * ahí, y el selector lo refleja.
   *
   * @param {Event} event
   */
  #handlePreviewZone = (event) => {
    const zone = event instanceof CustomEvent ? event.detail?.zone : '';
    if (!zone || zone === this.#iconTarget || !this.#zones.includes(zone)) return;

    this.#iconTarget = zone;
    this.#renderIconTarget();
  };

  /**
   * Selector «¿Dónde lo agregás?» sobre los íconos. Solo se ve cuando se
   * graba en más de una escena; con una sola, los íconos van ahí sin más.
   */
  #renderIconTarget() {
    const icons = this.querySelector('[data-mr-icons]');
    if (!(icons instanceof HTMLElement)) return;

    let box = this.querySelector('[data-mr-icon-target]');
    if (!(box instanceof HTMLElement)) {
      box = document.createElement('div');
      box.className = 'mr-icon-target';
      box.dataset.mrIconTarget = '';
      box.setAttribute('role', 'group');
      box.setAttribute('aria-label', ICON_TARGET_PROMPT);
      icons.before(box);
    }

    const zones = this.#zones;
    box.hidden = zones.length < 2;

    // Los botones se rehacen solo si cambian las escenas; si no, se marca el elegido
    const signature = zones.join(',');
    if (box.dataset.mrSignature !== signature) {
      const prompt = document.createElement('span');
      prompt.className = 'mr-icon-target__prompt';
      prompt.setAttribute('aria-hidden', 'true');
      prompt.textContent = ICON_TARGET_PROMPT;

      const buttons = zones.map((zone) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'mr-icon-target__option';
        button.dataset.mrIconTargetOption = zone;
        button.textContent = zoneLabel(zone) || zone;
        return button;
      });

      box.replaceChildren(prompt, ...buttons);
      box.dataset.mrSignature = signature;
    }

    for (const button of box.querySelectorAll('[data-mr-icon-target-option]')) {
      if (!(button instanceof HTMLElement)) continue;
      button.setAttribute('aria-pressed', String(button.dataset.mrIconTargetOption === this.#iconTarget));
    }
  }

  /**
   * «Personalizar» solo despliega las zonas: la variante sigue siendo la sin
   * grabado hasta que el cliente elige una (si hay una sola, se elige sola,
   * como en las bombillas). «Sin grabado» pliega el bloque y
   * vuelve al primer valor de la opción, que es el incluido.
   *
   * @param {boolean} open
   */
  #setOpen(open) {
    if (!open) {
      for (const input of this.querySelectorAll('[data-mr-cards="engraving"] .mr-card__input')) {
        if (input instanceof HTMLInputElement) input.checked = false;
      }

      const toggle = this.#secondToggle;
      if (toggle) toggle.checked = false;

      this.#selectOption(this.dataset.engravingOption, this.dataset.engravingNone);
      this.#selectOption(this.dataset.timeOption, this.dataset.timeNone);
    }

    this.#syncChoice(open);
    this.#syncSecond();

    // Con una sola forma de grabar no hay nada que elegir: se marca sola
    if (open && !this.#isEngraved) {
      const cards = this.querySelectorAll('[data-mr-cards="engraving"] .mr-card__input');
      const only = cards[0];
      if (cards.length === 1 && only instanceof HTMLInputElement && !only.disabled) {
        only.checked = true;
        only.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }
    }

    this.#togglePanels();
    this.#syncPreview();
  }

  /**
   * Pinta el estado de los dos botones de entrada y muestra u oculta la
   * tarjeta con las zonas y los campos.
   *
   * @param {boolean} open
   */
  #syncChoice(open) {
    this.dataset.open = String(open);

    const body = this.querySelector('[data-mr-body]');
    if (body instanceof HTMLElement) body.hidden = !open;

    for (const button of this.querySelectorAll('[data-mr-choice]')) {
      if (!(button instanceof HTMLElement)) continue;
      const custom = button.dataset.mrChoice === 'custom';
      button.setAttribute('aria-pressed', String(custom === open));
      if (custom) button.setAttribute('aria-expanded', String(open));
    }
  }

  /**
   * Marca el valor en el selector de variantes del tema. Ese input es el que
   * dispara todo lo demás, así que aquí no se toca nada más.
   *
   * @param {string | undefined} optionName
   * @param {string | undefined} value
   */
  #selectOption(optionName, value) {
    if (!optionName || !value) return;

    const picker = this.#section?.querySelector('variant-picker');
    if (!picker) return;

    const selector = `input[type="radio"][data-option-name="${CSS.escape(optionName)}"]`;
    const input = Array.from(picker.querySelectorAll(selector)).find(
      (candidate) => /** @type {HTMLInputElement} */ (candidate).value === value
    );

    if (!(input instanceof HTMLInputElement) || input.checked) return;

    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * Mete la figura dentro del texto, donde esté el cursor.
   *
   * Es un carácter más, no una pieza suelta: así el cliente coloca un único
   * bloque en la previsualización en vez de cuadrar varios elementos, y la
   * figura hereda sin más la tipografía y la curva del aro.
   *
   * @param {string} symbol
   */
  #insertSymbol(symbol) {
    if (!symbol) return;

    const field = this.#activeField ?? this.#field('[data-mr-text]');
    if (!field) return;

    const start = field.selectionStart ?? field.value.length;
    const end = field.selectionEnd ?? field.value.length;

    field.value = field.value.slice(0, start) + symbol + field.value.slice(end);
    field.selectionStart = field.selectionEnd = start + symbol.length;
    field.focus();

    this.#syncCounter();
    this.#syncFontSamples();
    this.#syncPreview();
    this.#togglePanels();
  }

  /**
   * Hasta dónde ha llegado el cliente:
   *
   *   0  sin grabado, no hay nada que configurar
   *   1  hay grabado: qué grabar (texto o logo)
   *   3  ya hay algo que grabar: cómo se graba y cuándo
   *
   * Los tramos se abren a medida que se elige, en vez de soltar el formulario
   * entero de golpe.
   */
  get #step() {
    if (!this.#isEngraved) return 0;

    const hasText = ['[data-mr-text]', '[data-mr-text4]'].some((selector) => Boolean(this.#field(selector)?.value.trim()));
    const hasLogo = ['[data-mr-logo]', '[data-mr-logo2]'].some((selector) => {
      const logo = this.querySelector(selector);
      return logo instanceof HTMLInputElement && (logo.files?.length ?? 0) > 0;
    });

    return hasText || hasLogo || this.#iconList.length > 0 ? 3 : 1;
  }

  /**
   * Lista de los íconos agregados, cada uno con su cruz para quitarlo. Con
   * copias repetidas, la cruz quita solo esa.
   */
  #renderIconList() {
    const list = this.querySelector('[data-mr-icons-list]');
    if (!(list instanceof HTMLElement)) return;

    const label = list.dataset.removeLabel || 'Quitar';

    // Con más de una escena, cada ícono dice en cuál está
    const preview = this.querySelector('mr-preview');
    const showZone = this.#zones.length > 1 && preview !== null && 'iconZone' in preview;
    const items = this.#iconList.map((item) => ({
      ...item,
      zone: showZone ? zoneLabel(/** @type {any} */ (preview).iconZone(item.uid)) : '',
    }));

    // Sin cambios no se rehace: se llama en cada tecla
    const signature = items.map((item) => `${item.uid}\u0000${item.zone}`).join('\u0001');
    if (list.dataset.mrSignature === signature) return;
    list.dataset.mrSignature = signature;
    list.replaceChildren();

    for (const item of items) {
      const button = document.createElement('button');
      const cross = document.createElement('span');

      button.type = 'button';
      button.className = 'mr-icons__chip';
      button.dataset.mrIconRemove = item.uid;
      button.setAttribute('aria-label', item.zone ? `${label}: ${item.name} (${item.zone})` : `${label}: ${item.name}`);
      button.textContent = item.name;

      if (item.zone) {
        const zone = document.createElement('span');
        zone.className = 'mr-icons__chip-zone';
        zone.textContent = ` · ${item.zone}`;
        button.append(zone);
      }

      cross.className = 'mr-icons__chip-cross';
      cross.setAttribute('aria-hidden', 'true');
      cross.textContent = '×';
      button.append(cross);

      list.append(button);
    }
  }

  /** Muestra los tramos alcanzados y desactiva lo que no debe viajar al carrito. */
  #togglePanels() {
    const step = this.#step;

    this.#toggleTextFields();

    for (const panel of this.querySelectorAll('[data-mr-step]')) {
      if (!(panel instanceof HTMLElement)) continue;
      panel.hidden = Number(panel.dataset.mrStep) > step;
    }

    // Un input desactivado no se envía: así ni «sin grabado» ni un tramo que
    // el cliente no ha llegado a ver mandan propiedades vacías al carrito.
    for (const field of this.querySelectorAll('[name^="properties["]')) {
      if (!(field instanceof HTMLInputElement) && !(field instanceof HTMLTextAreaElement)) continue;
      const panel = field.closest('[data-mr-step]');
      const own = field.closest('[data-mr-field]');
      const hidden = (panel instanceof HTMLElement && panel.hidden) || (own instanceof HTMLElement && own.hidden);
      field.disabled = panel instanceof HTMLElement ? hidden : !this.#isEngraved;
    }

    // Los íconos viajan al pedido por su nombre, uno por uno y separados por
    // comas, también los repetidos. Sin ninguno el campo no se envía.
    const iconsValue = this.querySelector('[data-mr-icons-value]');
    if (iconsValue instanceof HTMLInputElement) {
      iconsValue.value = this.#iconList.map((item) => item.name).join(', ');
      if (!this.#iconList.length) iconsValue.disabled = true;
    }

    if (step === 0) this.#clearErrors();

    this.#syncRequirement();
  }

  /**
   * Qué le falta al cliente para poder añadir al carrito con «Personalizar»
   * abierto: la zona, o algo que grabar (texto o logo, según el ajuste).
   * Grabando virola y costado, hace falta algo en cada una: «virola» o
   * «cuerpo» dicen cuál está vacía. Cadena vacía si no falta nada o si eligió
   * no personalizar.
   */
  get #missing() {
    if (this.dataset.open !== 'true') return '';
    if (!this.#isEngraved) return 'zone';

    const mode = this.dataset.requiredMode || 'text_or_logo';
    if (mode === 'none') return '';

    const primary = this.#missingPrimary(mode);
    if (primary || !this.#secondActive) return primary;

    // Otro costado: su texto, su logo o un ícono puesto en su escena
    const text = this.#field('[data-mr-text4]');
    const logo = this.querySelector('[data-mr-logo2]');
    const hasText = Boolean(text && !text.disabled && text.value.trim());
    const hasLogo = logo instanceof HTMLInputElement && (logo.files?.length ?? 0) > 0;
    const extras = mode === 'text_or_logo';
    const hasIcon = this.#iconZones.has(this.#secondZone);

    return hasText || (extras && (hasLogo || hasIcon)) ? '' : 'side2';
  }

  /**
   * Lo que falta en la cara principal (la de la tarjeta).
   *
   * @param {string} mode
   */
  #missingPrimary(mode) {
    /** @param {string} selector */
    const filled = (selector) => {
      const field = this.#field(selector);
      return Boolean(field && !field.disabled && field.value.trim());
    };

    // Un logo o un ícono también son algo que grabar
    const logo = this.querySelector('[data-mr-logo]');
    const hasLogo = logo instanceof HTMLInputElement && (logo.files?.length ?? 0) > 0;
    const extras = mode === 'text_or_logo';

    const zones = this.#baseZones;
    if (zones.includes('virola') && zones.includes('cuerpo')) {
      // El logo va siempre al costado; cada ícono, a la zona donde se puso
      const iconZones = this.#iconZones;
      const ring = filled('[data-mr-text]') || filled('[data-mr-text2]') || (extras && iconZones.has('virola'));
      if (!ring) return 'virola';

      const side = filled('[data-mr-text3]') || (extras && (hasLogo || iconZones.has('cuerpo')));
      return side ? '' : 'cuerpo';
    }

    if (['[data-mr-text]', '[data-mr-text2]', '[data-mr-text3]'].some(filled)) return '';
    // Los íconos del otro costado no cuentan para la cara principal
    const iconZones = this.#iconZones;
    const primaryIcon = [...iconZones].some((zone) => zone !== this.#secondZone);
    if (extras && (hasLogo || primaryIcon)) return '';

    return 'content';
  }

  /**
   * Zonas con al menos un ícono. Lo sabe la previsualización, que es quien
   * reparte los íconos; sin ella, los íconos cuentan para las dos zonas.
   */
  get #iconZones() {
    const preview = this.querySelector('mr-preview');
    const zones = preview && 'zonesWithIcons' in preview ? preview.zonesWithIcons : null;
    if (zones instanceof Set) return zones;

    return new Set(this.#iconList.length ? ['virola', 'cuerpo', 'termo'] : []);
  }

  /** Muestra el aviso de lo que falta y bloquea o libera el botón de compra. */
  #syncRequirement() {
    const missing = this.#missing;

    const zoneError = this.querySelector('[data-mr-zone-error]');
    if (zoneError instanceof HTMLElement) zoneError.hidden = missing !== 'zone';

    const contentError = this.querySelector('[data-mr-error]');
    if (contentError instanceof HTMLElement) {
      const zoneMessages = (this.dataset.zonesRequiredMessages || '').split('|');
      const general = this.dataset.requiredMessage ?? '';

      contentError.textContent =
        missing === 'virola'
          ? zoneMessages[0] || general
          : missing === 'cuerpo'
            ? zoneMessages[1] || general
            : missing === 'side2'
              ? this.dataset.secondRequiredMessage || general
              : general;
      contentError.hidden = !['content', 'virola', 'cuerpo', 'side2'].includes(missing);

      // El aviso se muestra junto al campo que falta: el del costado es otro
      const field = this.#field(this.#missingFieldSelector(missing));
      const place = field?.closest('.mr-field');
      if (place && contentError.parentElement !== place) place.append(contentError);
    }

    this.#setCartBlocked(Boolean(missing));
  }

  /**
   * Campo donde se muestra el aviso de lo que falta.
   *
   * @param {string} missing
   */
  #missingFieldSelector(missing) {
    if (missing === 'cuerpo') return '[data-mr-text3]';
    if (missing === 'side2') return '[data-mr-text4]';
    return '[data-mr-text]';
  }

  /**
   * Bloquea los botones de añadir al carrito de la sección —el de la ficha y
   * el de la barra fija—. Se ven y se anuncian desactivados, pero no llevan
   * `disabled`: un botón desactivado no recibe el toque, y ese toque es el
   * que dispara el aviso. El envío lo frenan #handleBlockedClick y
   * #handleSubmit. Un botón apagado por falta de stock se deja como está.
   *
   * @param {boolean} blocked
   */
  #setCartBlocked(blocked) {
    this.#blocked = blocked;

    for (const button of this.#section?.querySelectorAll('[ref="addToCartButton"]') ?? []) {
      if (!(button instanceof HTMLButtonElement)) continue;

      if (blocked && !button.disabled && !button.dataset.mrBlocked) {
        button.dataset.mrBlocked = 'true';
        button.setAttribute('aria-disabled', 'true');
      } else if (!blocked && button.dataset.mrBlocked) {
        delete button.dataset.mrBlocked;
        button.removeAttribute('aria-disabled');
      }
    }
  }

  /**
   * Toque en un botón de compra bloqueado: no se añade nada y se le muestra
   * al cliente qué le falta.
   *
   * @param {MouseEvent} event
   */
  #handleBlockedClick = (event) => {
    if (!this.#blocked || !(event.target instanceof Element)) return;

    const button = event.target.closest('[ref="addToCartButton"]');
    if (!(button instanceof HTMLElement) || !button.dataset.mrBlocked) return;
    if (!this.#section?.contains(button)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.#nudge();
  };

  /**
   * Red de seguridad: si el formulario se envía igualmente (con Intro en la
   * cantidad, por ejemplo), se frena mientras falten datos del grabado.
   *
   * @param {SubmitEvent} event
   */
  #handleSubmit = (event) => {
    if (!this.#blocked || !(event.target instanceof HTMLFormElement)) return;
    if (event.target.id !== this.dataset.formId) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.#nudge();
  };

  /**
   * Micro interacción: lleva al cliente a lo que le falta —las zonas o el
   * texto— y lo marca con un temblor corto y el borde resaltado. Si el bloque
   * está fuera de pantalla, primero se desplaza hasta él. Con «reducir
   * movimiento» no hay temblor ni desplazamiento suave: solo el resaltado.
   */
  #nudge() {
    this.#syncRequirement();

    const missing = this.#missing;
    if (!missing) return;

    const target =
      missing === 'zone'
        ? this.querySelector('[data-mr-cards="engraving"]')
        : this.#field(this.#missingFieldSelector(missing));
    if (!(target instanceof HTMLElement)) return;

    const message = this.querySelector(missing === 'zone' ? '[data-mr-zone-error]' : '[data-mr-error]');
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const mark = () => {
      for (const element of [target, message]) {
        if (!(element instanceof HTMLElement)) continue;
        element.classList.remove('mr-nudge');
        // Fuerza un nuevo ciclo para que la animación se repita en cada toque
        void element.offsetWidth;
        element.classList.add('mr-nudge');
      }

      if (target instanceof HTMLTextAreaElement) target.focus({ preventScroll: true });

      clearTimeout(this.#nudgeTimer);
      this.#nudgeTimer = window.setTimeout(() => {
        target.classList.remove('mr-nudge');
        if (message instanceof HTMLElement) message.classList.remove('mr-nudge');
      }, 1600);
    };

    const rect = target.getBoundingClientRect();
    const visible = rect.top >= 0 && rect.bottom <= window.innerHeight;

    if (visible) {
      mark();
      return;
    }

    target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
    window.setTimeout(mark, reduce ? 0 : 450);
  }

  /** La lista de tipografías se cierra al tocar fuera de ella. */
  #handleOutsideClick = (event) => {
    const sheet = this.querySelector('[data-mr-fonts]');
    if (!sheet || sheet.getAttribute('data-open') !== 'true') return;

    const target = event.target;
    const toggle = this.querySelector('[data-mr-fonts-toggle]');
    if (target instanceof Node && (sheet.contains(target) || toggle?.contains(target))) return;

    this.#closeFontSheet();
  };

  /** @param {HTMLInputElement} choice */
  #applyMaxLength(choice) {
    const input = this.#field('[data-mr-text]');
    if (!input) return;

    const max = Number(choice.dataset.mrMaxLength) || Number(this.dataset.defaultMaxLength) || 20;
    input.maxLength = max;
    if (input.value.length > max) input.value = input.value.slice(0, max);

    this.#syncCounter();
  }

  #syncCounter() {
    const input = this.#field('[data-mr-text]');
    const counter = this.querySelector('[data-mr-counter]');
    if (!input || !counter) return;

    counter.textContent = `${input.value.length}/${input.maxLength}`;
  }

  /** La muestra de cada tipografía usa el texto del cliente en cuanto lo hay. */
  #syncFontSamples() {
    const text = this.#field('[data-mr-text]')?.value.trim() ?? '';

    for (const sample of this.querySelectorAll('[data-mr-font-sample]')) {
      if (!(sample instanceof HTMLElement)) continue;
      if (!sample.dataset.mrDefault) sample.dataset.mrDefault = sample.textContent ?? '';
      sample.textContent = text || sample.dataset.mrDefault;
    }

    this.#syncFont();
  }

  /** Copia la tipografía elegida al campo oculto y al resumen de móvil. */
  #syncFont() {
    const checked = /** @type {HTMLInputElement | null} */ (this.querySelector('.mr-font__input:checked'));
    const value = this.querySelector('[data-mr-font-value]');

    if (value instanceof HTMLInputElement) value.value = checked?.value ?? '';

    const current = this.querySelector('[data-mr-current-sample]');
    if (current instanceof HTMLElement) {
      const sample = checked?.closest('.mr-font')?.querySelector('[data-mr-font-sample]');
      current.textContent = sample?.textContent ?? '';
      current.style.fontFamily = checked?.dataset.mrFontFamily ?? '';
    }

    this.#syncPreview();
  }

  #closeFontSheet() {
    this.querySelector('[data-mr-fonts]')?.setAttribute('data-open', 'false');
    this.querySelector('[data-mr-fonts-toggle]')?.setAttribute('aria-expanded', 'false');
  }

  /**
   * Pasa a la previsualización lo que necesita: qué se graba, con qué
   * tipografía y sobre qué zonas, con su área grabable. Colocar los textos,
   * curvarlos sobre el aro y generar la imagen para el taller es cosa suya,
   * en assets/mr-preview.js.
   */
  #syncPreview() {
    const preview = this.querySelector('mr-preview');
    if (!(preview instanceof HTMLElement)) return;

    const choice = this.#engravingChoice;
    const baseZones = this.#baseZones;
    const zones = this.#zones;
    const areas = (choice?.dataset.mrGeometry || '').split(';').map((area) => area.trim());

    // El otro costado («cuerpo2», «termo2») usa el área de su costado
    for (const stage of preview.querySelectorAll('[data-mr-stage]')) {
      if (!(stage instanceof HTMLElement)) continue;
      const zone = (stage.dataset.zone ?? '').replace(/2$/, '');
      const index = baseZones.indexOf(zone);
      if (index >= 0 && areas[index]) stage.dataset.geometry = areas[index];
    }

    const checked = /** @type {HTMLInputElement | null} */ (this.querySelector('.mr-font__input:checked'));

    preview.dataset.font = checked?.dataset.mrFontFamily ?? '';
    preview.dataset.text1 = this.#field('[data-mr-text]')?.value ?? '';
    preview.dataset.text2 = this.#field('[data-mr-text2]')?.value ?? '';
    preview.dataset.text3 = this.#field('[data-mr-text3]')?.value ?? '';
    preview.dataset.text4 = this.#secondActive ? this.#field('[data-mr-text4]')?.value ?? '' : '';

    // El logo subido se dibuja sobre la foto del cuerpo, no solo se adjunta
    const logo = this.querySelector('[data-mr-logo]');
    const file = logo instanceof HTMLInputElement ? logo.files?.[0] : null;

    if (file && this.#logoFile !== file) {
      URL.revokeObjectURL(preview.dataset.logo ?? '');
      preview.dataset.logo = URL.createObjectURL(file);
      this.#logoFile = file;
    } else if (!file && this.#logoFile) {
      URL.revokeObjectURL(preview.dataset.logo ?? '');
      preview.dataset.logo = '';
      this.#logoFile = null;
    }

    // El logo del otro costado, solo con la casilla marcada
    const logo2 = this.querySelector('[data-mr-logo2]');
    const file2 = this.#secondActive && logo2 instanceof HTMLInputElement ? logo2.files?.[0] : null;

    if (file2 && this.#logo2File !== file2) {
      URL.revokeObjectURL(preview.dataset.logo2 ?? '');
      preview.dataset.logo2 = URL.createObjectURL(file2);
      this.#logo2File = file2;
    } else if (!file2 && this.#logo2File) {
      URL.revokeObjectURL(preview.dataset.logo2 ?? '');
      preview.dataset.logo2 = '';
      this.#logo2File = null;
    }

    // Los íconos nuevos entran en la escena elegida en el selector
    if (!zones.includes(this.#iconTarget)) this.#iconTarget = zones[0] ?? '';
    if ('targetZone' in preview) /** @type {any} */ (preview).targetZone = this.#iconTarget;

    preview.dataset.zones = zones.join(',');
    preview.dataset.icons = this.#iconList.map((item) => item.uid).join(',');

    this.#renderIconTarget();
    this.#renderIconList();

    // La previsualización acaba de repartir los íconos entre las zonas: con
    // virola y costado, eso decide si a alguna le falta algo que grabar.
    this.#syncRequirement();
  }

  /**
   * La tipografía que no admite números se desactiva en cuanto el texto lleva
   * dígitos, y avisa si ya estaba elegida.
   */
  #checkNumbers() {
    const input = this.#field('[data-mr-text]');
    const hasDigits = Boolean(input && /\d/.test(input.value));
    const error = this.querySelector('[data-mr-font-error]');
    let conflict = false;

    for (const option of this.querySelectorAll('.mr-font__input')) {
      if (!(option instanceof HTMLInputElement)) continue;
      if (option.dataset.mrAllowsNumbers === 'false') {
        option.disabled = hasDigits;
        if (hasDigits && option.checked) conflict = true;
      }
    }

    if (error instanceof HTMLElement) {
      error.textContent = conflict ? this.dataset.numbersWarning ?? '' : '';
      error.hidden = !conflict;
    }
  }

  /**
   * El logo no viaja al pedido tal cual lo sube el cliente: se comprueba el
   * formato y el peso, se abre como imagen y se adjunta una copia nueva hecha
   * solo con sus píxeles. Lo que no se pueda abrir como imagen se rechaza, y
   * lo que llevara escondido el archivo original (código, datos de la foto
   * como la ubicación GPS) no pasa a la copia.
   *
   * Mientras se prepara la copia, el botón de compra queda bloqueado para que
   * no salga el archivo original.
   *
   * @param {HTMLInputElement} input
   */
  async #processLogo(input) {
    const name = input.closest('.mr-field')?.querySelector('[data-mr-logo-name]');
    const file = input.files?.[0];
    const maxMb = Number(input.dataset.mrMaxSize) || 5;
    const turn = (this.#logoTurns.get(input) ?? 0) + 1;
    this.#logoTurns.set(input, turn);

    // La primera vez el rótulo todavía muestra la llamada; se guarda para
    // devolverlo cuando se rechaza un archivo.
    if (name instanceof HTMLElement && name.dataset.mrDefault === undefined) {
      name.dataset.mrDefault = name.textContent ?? '';
    }

    if (!file) {
      this.#showLogoError(input, '');
      this.#togglePanels();
      this.#syncPreview();
      return;
    }

    const extension = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
    if (!LOGO_TYPES.includes(file.type) || !LOGO_EXTENSIONS.includes(extension)) {
      this.#rejectLogo(input, 'Solo se aceptan imágenes JPG, PNG o WebP.');
      return;
    }

    if (file.size > maxMb * 1024 * 1024) {
      this.#rejectLogo(input, `El archivo supera los ${maxMb} MB.`);
      return;
    }

    this.#setCartBlocked(true);

    let clean = null;
    try {
      clean = await this.#cleanLogo(file);
    } catch {
      clean = null;
    }

    // Si mientras tanto eligió otra imagen, esta ya no cuenta
    if (turn !== this.#logoTurns.get(input)) return;

    if (!clean) {
      this.#rejectLogo(input, 'No hemos podido abrir esta imagen. Prueba con otra.');
      return;
    }

    try {
      const transfer = new DataTransfer();
      transfer.items.add(clean);
      input.files = transfer.files;
    } catch {
      this.#rejectLogo(input, 'Tu navegador no permite preparar la imagen. Actualízalo o prueba con otro.');
      return;
    }

    this.#showLogoError(input, '');
    if (name instanceof HTMLElement) name.textContent = file.name;
    this.#togglePanels();
    this.#syncPreview();
  }

  /**
   * Copia limpia del logo: se dibuja en un lienzo y se exporta de nuevo. Un
   * PNG sale en PNG, para conservar la transparencia; un JPG o un WebP sale en
   * JPG sobre fondo blanco. El navegador ya aplica el giro de la foto al
   * dibujarla, así que la copia sale derecha aunque pierda esos datos.
   *
   * @param {File} file
   * @returns {Promise<File | null>}
   */
  async #cleanLogo(file) {
    const url = URL.createObjectURL(file);

    try {
      const image = new Image();
      image.src = url;
      await image.decode();

      let width = image.naturalWidth;
      let height = image.naturalHeight;
      if (!width || !height) return null;

      const scale = Math.min(1, Math.sqrt(LOGO_MAX_PIXELS / (width * height)));
      width = Math.max(1, Math.round(width * scale));
      height = Math.max(1, Math.round(height * scale));

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext('2d');
      if (!context) return null;

      const png = file.type === 'image/png';
      if (!png) {
        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, width, height);
      }
      context.drawImage(image, 0, 0, width, height);

      const type = png ? 'image/png' : 'image/jpeg';
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, type, 0.92));
      if (!(blob instanceof Blob)) return null;

      const base = file.name.slice(0, file.name.lastIndexOf('.')) || 'logo';
      return new File([blob], `${base}${png ? '.png' : '.jpg'}`, { type, lastModified: Date.now() });
    } finally {
      URL.revokeObjectURL(url);
    }
  }

  /**
   * @param {HTMLInputElement} input
   * @param {string} message
   */
  #rejectLogo(input, message) {
    input.value = '';

    const name = input.closest('.mr-field')?.querySelector('[data-mr-logo-name]');
    if (name instanceof HTMLElement) name.textContent = name.dataset.mrDefault ?? '';

    this.#showLogoError(input, message);
    this.#togglePanels();
    this.#syncPreview();
  }

  /**
   * @param {HTMLInputElement} input - El campo del logo, para avisar junto a él.
   * @param {string} message - Vacío para ocultar el aviso.
   */
  #showLogoError(input, message) {
    const error = input.closest('.mr-field')?.querySelector('[data-mr-logo-error]');
    if (!(error instanceof HTMLElement)) return;

    error.textContent = message;
    error.hidden = !message;
  }

  #clearErrors() {
    for (const error of this.querySelectorAll('[data-mr-error], [data-mr-font-error], [data-mr-logo-error]')) {
      if (error instanceof HTMLElement) error.hidden = true;
    }
  }

  /**
   * Al cambiar la variante desde fuera (el color, por ejemplo) los precios y
   * la disponibilidad de las tarjetas cambian. Se sustituyen solo las tarjetas
   * con el HTML ya renderizado por Liquid; los campos que el cliente ha
   * rellenado y el estado de los botones de entrada no se tocan.
   *
   * @param {CustomEvent & {promise: Promise<any>}} event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const fresh = detail.html.querySelector('mr-engraving');
        if (!fresh) return;

        for (const group of ['engraving', 'time']) {
          const current = this.querySelector(`[data-mr-cards="${group}"]`);
          const updated = fresh.querySelector(`[data-mr-cards="${group}"]`);
          if (current && updated) current.replaceWith(updated);
        }

        this.#syncChoice(this.dataset.open === 'true' || this.#isEngraved);
        this.#syncSecond();
        this.#togglePanels();
        this.#syncPreview();

        // El tema repinta el botón de compra en este mismo cambio: se vuelve
        // a aplicar el bloqueo cuando ya terminó.
        requestAnimationFrame(() => this.#syncRequirement());
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[mr-engraving] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('mr-engraving')) {
  customElements.define('mr-engraving', MrEngraving);
}
